import { IPurchaseOrder } from "../../types/purchaseOrder.types";
import { ApiResponseType, makeRequest } from "../helpers/api.helpers";
import { createDebounce } from "../helpers/debounce.helper";
import { getCompanyId } from "./company.api";

const API_URL = import.meta.env.VITE_API_URL;
const makeRequestDebounced = createDebounce(makeRequest, 200);

export type GetPurchaseOrdersOptions = {
  status?: string[];
  search?: string;
  page?: number;
  itemsPerPage?: string;
  orderBy?: string;
  fields?: string;
  supplierId?: string;
};

export async function getPurchaseOrders(
  options: GetPurchaseOrdersOptions = {}
) {
  const {
    status = [],
    search = "",
    page = 1,
    itemsPerPage = "10",
    orderBy = "updatedAt",
    fields = "_id, description, code, supplierName, requestorName, financial, itens, inventoryLaunched, createdAt, status, votesApproved, companyId, isCounted",
    supplierId,
  } = options;
  const params = new URLSearchParams({
    page: String(page),
    itemsPerPage: itemsPerPage,
    fields,
  });
  let statusParam = "";
  if (Array.isArray(status) && status.length > 0) {
    statusParam = status.join(",");
  } else if (typeof status === "string") {
    statusParam = status;
  }
  status && params.append("filters[status]", statusParam);
  search && params.append("search", search);
  orderBy && params.append("orderBy", orderBy);
  supplierId && params.append("filters[supplierId]", supplierId);

  const url = `${API_URL}/purchase-order?${params.toString()}`;
  const result = (await makeRequestDebounced(url)) as ApiResponseType<
    IPurchaseOrder[]
  > & {
    totalItems: number;
    statusCounter: string;
  };

  return {
    ok: result.ok,
    data: result.data,
    totalItems: result.totalItems,
    statusCounter: result.statusCounter,
  };
}

export function getPurchaseOrderById(id: string) {
  const url = `${API_URL}/purchase-order/${id}`;
  return makeRequestDebounced(url) as Promise<ApiResponseType<IPurchaseOrder>>;
}

export async function approvePurchaseOrder(data: {
  _id: string;
  requestorName: string;
  requestorEmail: string;
}) {
  const url = `${API_URL}/purchase-order/approve`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IPurchaseOrder>
  >;

  return result;
}

export async function rejectPurchaseOrder(_id: string) {
  const url = `${API_URL}/purchase-order/reject`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ _id }),
  };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IPurchaseOrder>
  >;

  return result;
}

export async function cancelPurchaseOrder(_id: string) {
  const url = `${API_URL}/purchase-order/cancel`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ _id }),
  };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IPurchaseOrder>
  >;

  return result;
}

export async function createPurchaseOrder(
  data: IPurchaseOrder,
  isSplit = false
) {
  const url = `${API_URL}/purchase-order?isSplit=${isSplit}`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ...data,
      companyId: data.companyId || getCompanyId(),
    }),
  };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IPurchaseOrder> & { code: number }
  >;

  return result;
}

export async function updatePurchaseOrder(data: IPurchaseOrder) {
  const dataToUpdated = {
    ...data,
    companyId: getCompanyId(),
  };

  const url = `${API_URL}/purchase-order`;
  const options = {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(dataToUpdated),
  };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IPurchaseOrder>
  >;

  return result;
}

export async function searchPurchaseOrderByName(name: string) {
  const params = new URLSearchParams({
    productName: name,
  });

  const url = `${API_URL}/purchase-order-search?${params.toString()}`;
  const result = (await makeRequestDebounced(url)) as ApiResponseType<
    IPurchaseOrder[]
  >;

  return { ok: result.ok, data: result.data };
}

export async function attachBilletFileInSchedulePayment(
  data: IPurchaseOrder,
  files: FileList,
  additionalFile?: boolean
) {
  const formData = new FormData();

  for (const file of files) {
    formData.append("file[]", file);
  }

  formData.append("data", JSON.stringify(data));
  formData.append("additionalFile", additionalFile ? "true" : "false");

  const url = `${API_URL}/purchase-order/send-billetFile`;
  const options = {
    method: "POST",
    body: formData,
  };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<{
      urls: { name: string; url: string }[];
      isPresigned: boolean;
    }>
  >;
  return result;
}

export async function awsPresignedUrls(files) {

  const formData = new FormData();
  formData.append("files[]", files);
  const url = `${API_URL}/aws/presigned-urls`;
  const options = { method: "POST", body: formData };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<{
      urls: { name: string; url: string }[];
      isPresigned: boolean;
    }>
  >;

  return result;
}

export async function uploadFilePresignedUrl(file: File, presignedUrl: string) {
  const response = await fetch(presignedUrl, {
    method: "PUT",
    headers: {
      "Content-Type": file.type || "application/octet-stream",
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error("Upload failed");
  }

  return { ok: response.ok }
}


export async function DeleteFilePurchaseOrder(data: {file: string, purchaseOrderId: string}) {
  const url = `${API_URL}/purchase-order/deleteFile`;
  const options = {
    method: 'PATCH',
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IPurchaseOrder>
  >;
  return result
}

export async function revalidar(id: string) {
  const url = `${API_URL}/purchase-order/revalidate`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id: id }),
  };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IPurchaseOrder>
  >;
  return result;
}

export async function purchaseOrderInventoryLaunched(_id: string) {
  const url = `${API_URL}/purchase-order/postInventory`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ _id: _id }),
  };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IPurchaseOrder>
  >;

  return result;
}

export type SchedulePayment = {
  stakeholderId: string;
  description: string;
  dueDate: Date;
  scheduleDate: Date;
  categories: { categoryId: string; value: string }[];
  costCenters: { costCenterId: string }[];
  instalment: {
    installmentNumber: number;
    value: number;
    dueDate: Date;
  };
  reference: string;
};
